/**
 * Book Viewer Component
 * Displays multi-page brochures with book-style two-page spread and page-turning animation
 * 
 * Features:
 * - Two-page spread layout (left/right pages)
 * - Page navigation (Previous/Next, keyboard arrows)
 * - Page-turning animation
 * - Fullscreen mode
 * - Zoom controls
 * - Mobile swipe gestures
 * - Page counter
 */

'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Minimize,
  BookOpen, Loader
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface Page {
  id: number;
  page_number: number;
  file_url: string;
  file_size: number;
  file_format: string;
  is_thumbnail: boolean;
}

interface DocumentInfo {
  id: number;
  title: string;
  description?: string;
  page_count: number;
}

interface BookViewerProps {
  documentId: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function BookViewer({
  documentId,
  isOpen,
  onClose
}: BookViewerProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  
  const [document, setDocument] = useState<DocumentInfo | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [currentSpread, setCurrentSpread] = useState(0); // Which spread (pair of pages) to show
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);

  // Authentication helper
  const getAuthToken = (): string | null => {
    try {
      const storedTokens = localStorage.getItem('auth_tokens');
      if (storedTokens) {
        const tokens = JSON.parse(storedTokens);
        return tokens.access_token;
      }
    } catch (error) {
      console.error('[BookViewer] Failed to load auth token:', error);
    }
    return null;
  };

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(
        `/api/v1/finance/reference-documents/${documentId}/pages`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load document pages');
      }

      const result = await response.json();

      if (result.success && result.data) {
        setDocument(result.data.document);
        setPages(result.data.pages);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load pages');
      console.error('Error loading pages:', err);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const previousSpread = useCallback(() => {
    if (currentSpread > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentSpread(prev => prev - 1);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [currentSpread, isTransitioning]);

  const nextSpread = useCallback(() => {
    const maxSpread = Math.ceil(pages.length / 2) - 1;
    if (currentSpread < maxSpread && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentSpread(prev => prev + 1);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [currentSpread, isTransitioning, pages.length]);

  const toggleFullscreen = () => {
    setFullscreen(prev => !prev);
  };

  // Load pages when modal opens
  useEffect(() => {
    if (isOpen && documentId) {
      loadPages();
    }
  }, [isOpen, documentId, loadPages]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        previousSpread();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        nextSpread();
      } else if (e.key === 'Escape') {
        if (fullscreen) {
          setFullscreen(false);
        } else {
          onClose();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentSpread, pages.length, fullscreen, nextSpread, onClose, previousSpread]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setCurrentSpread(0);
      setZoom(1);
      setFullscreen(false);
      setError(null);
    }
  }, [isOpen]);

  const zoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 2));
  };

  const zoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  // Touch gesture handling for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    // Swipe threshold: 50px
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSpread(); // Swipe left = next
      } else {
        previousSpread(); // Swipe right = previous
      }
    }
  };

  // Calculate current pages being shown
  const leftPageIndex = currentSpread * 2;
  const rightPageIndex = leftPageIndex + 1;
  const leftPage = pages[leftPageIndex];
  const rightPage = pages[rightPageIndex];
  
  const totalSpreads = Math.ceil(pages.length / 2);
  const isFirstSpread = currentSpread === 0;
  const isLastSpread = currentSpread === totalSpreads - 1;

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        fullscreen ? 'bg-black' : 'bg-black/80'
      }`}
      onClick={(e) => {
        // Close on backdrop click (not in fullscreen)
        if (e.target === e.currentTarget && !fullscreen) {
          onClose();
        }
      }}
    >
      <div 
        ref={containerRef}
        className={`flex flex-col ${
          fullscreen 
            ? 'w-full h-full' 
            : 'w-[95vw] h-[95vh] max-w-7xl rounded-lg overflow-hidden'
        } ${isDark ? 'bg-gray-900' : 'bg-white'}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <BookOpen size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
            <div className="min-w-0">
              <h3 className={`font-semibold text-sm md:text-base truncate ${
                isDark ? 'text-gray-200' : 'text-gray-900'
              }`}>
                {document?.title || 'Loading...'}
              </h3>
              {document?.description && (
                <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                  {document.description}
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className={`p-2 rounded-lg flex-shrink-0 ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex items-center justify-center overflow-hidden p-4 md:p-8 relative">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader size={32} className="animate-spin text-blue-600" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading pages...
              </p>
            </div>
          ) : error ? (
            <div className="text-center">
              <p className={`text-sm mb-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                {error}
              </p>
              <button
                onClick={loadPages}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : pages.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              No pages found
            </p>
          ) : (
            <>
              {/* Two-Page Spread */}
              <div 
                className="flex items-center justify-center gap-2 md:gap-6 max-w-full max-h-full"
                style={{
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.3s ease'
                }}
              >
                {/* Left Page */}
                <div 
                  className={`relative ${
                    isTransitioning ? 'animate-page-turn-left' : ''
                  }`}
                  style={{
                    width: fullscreen ? '45vw' : 'min(45vw, 400px)',
                    aspectRatio: '3/4'
                  }}
                >
                  {leftPage ? (
                    <div className="relative w-full h-full">
                      <img
                        src={leftPage.file_url}
                        alt={`Page ${leftPage.page_number}`}
                        className="w-full h-full object-contain bg-white rounded-l-lg shadow-2xl"
                      />
                      {/* Page Number Overlay */}
                      <div className={`absolute bottom-4 left-4 px-2 py-1 rounded text-xs font-medium ${
                        isDark ? 'bg-gray-800/80 text-gray-300' : 'bg-white/80 text-gray-700'
                      }`}>
                        {leftPage.page_number}
                      </div>
                    </div>
                  ) : (
                    <div className={`w-full h-full rounded-l-lg flex items-center justify-center ${
                      isDark ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <span className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        {/* Empty left page */}
                      </span>
                    </div>
                  )}
                </div>

                {/* Center Spine */}
                <div 
                  className={`w-1 self-stretch rounded ${
                    isDark ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                  style={{ minHeight: '60vh' }}
                />

                {/* Right Page */}
                <div 
                  className={`relative ${
                    isTransitioning ? 'animate-page-turn-right' : ''
                  }`}
                  style={{
                    width: fullscreen ? '45vw' : 'min(45vw, 400px)',
                    aspectRatio: '3/4'
                  }}
                >
                  {rightPage ? (
                    <div className="relative w-full h-full">
                      <img
                        src={rightPage.file_url}
                        alt={`Page ${rightPage.page_number}`}
                        className="w-full h-full object-contain bg-white rounded-r-lg shadow-2xl"
                      />
                      {/* Page Number Overlay */}
                      <div className={`absolute bottom-4 right-4 px-2 py-1 rounded text-xs font-medium ${
                        isDark ? 'bg-gray-800/80 text-gray-300' : 'bg-white/80 text-gray-700'
                      }`}>
                        {rightPage.page_number}
                      </div>
                    </div>
                  ) : (
                    <div className={`w-full h-full rounded-r-lg flex items-center justify-center ${
                      isDark ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <span className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        {/* Empty right page */}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Arrows (Desktop) */}
              {!isFirstSpread && (
                <button
                  onClick={previousSpread}
                  disabled={isTransitioning}
                  className={`hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full ${
                    isDark 
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                      : 'bg-white hover:bg-gray-100 text-gray-900'
                  } shadow-lg disabled:opacity-50 transition-all hover:scale-110`}
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              
              {!isLastSpread && (
                <button
                  onClick={nextSpread}
                  disabled={isTransitioning}
                  className={`hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full ${
                    isDark 
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                      : 'bg-white hover:bg-gray-100 text-gray-900'
                  } shadow-lg disabled:opacity-50 transition-all hover:scale-110`}
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer Controls */}
        {!loading && !error && pages.length > 0 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${
            isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
          }`}>
            {/* Navigation (Mobile) */}
            <div className="flex items-center gap-2">
              <button
                onClick={previousSpread}
                disabled={isFirstSpread || isTransitioning}
                className={`px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1 ${
                  isFirstSpread || isTransitioning
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <ChevronLeft size={16} />
                <span className="hidden sm:inline">Previous</span>
              </button>
              
              {/* Page Counter */}
              <div className={`px-3 py-2 text-sm font-medium ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Pages {leftPage?.page_number || '?'}-{rightPage?.page_number || leftPage?.page_number || '?'} of {pages.length}
              </div>
              
              <button
                onClick={nextSpread}
                disabled={isLastSpread || isTransitioning}
                className={`px-3 py-2 rounded-lg font-medium text-sm flex items-center gap-1 ${
                  isLastSpread || isTransitioning
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Zoom & Fullscreen Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={zoomOut}
                disabled={zoom <= 0.5}
                className={`p-2 rounded-lg ${
                  zoom <= 0.5
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                      ? 'hover:bg-gray-700'
                      : 'hover:bg-gray-200'
                }`}
                title="Zoom out"
              >
                <ZoomOut size={18} />
              </button>
              
              <span className={`text-xs font-medium w-12 text-center ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {Math.round(zoom * 100)}%
              </span>
              
              <button
                onClick={zoomIn}
                disabled={zoom >= 2}
                className={`p-2 rounded-lg ${
                  zoom >= 2
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                      ? 'hover:bg-gray-700'
                      : 'hover:bg-gray-200'
                }`}
                title="Zoom in"
              >
                <ZoomIn size={18} />
              </button>
              
              <div className={`w-px h-6 mx-1 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
              
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg ${
                  isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
                title={fullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
              >
                {fullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS for page-turning animation */}
      <style>{`
        @keyframes pageTurnLeft {
          0% {
            transform: perspective(1000px) rotateY(0deg);
            opacity: 1;
          }
          50% {
            transform: perspective(1000px) rotateY(-90deg);
            opacity: 0.5;
          }
          100% {
            transform: perspective(1000px) rotateY(0deg);
            opacity: 1;
          }
        }

        @keyframes pageTurnRight {
          0% {
            transform: perspective(1000px) rotateY(0deg);
            opacity: 1;
          }
          50% {
            transform: perspective(1000px) rotateY(90deg);
            opacity: 0.5;
          }
          100% {
            transform: perspective(1000px) rotateY(0deg);
            opacity: 1;
          }
        }

        .animate-page-turn-left {
          animation: pageTurnLeft 0.6s ease-in-out;
        }

        .animate-page-turn-right {
          animation: pageTurnRight 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
}
